import React, { useState, useRef, useCallback } from 'react';
import { Plus, ChevronDown, ChevronRight, Trash2, Palette, X, Check } from 'lucide-react';
import { NODE_COLORS } from './useMindMap';

const MindMapNode = ({
  node,
  isSelected,
  isEditing,
  isConnectorSource,
  hasChildren,
  onSelect,
  onStartEdit,
  onEndEdit,
  onUpdate,
  onAddChild,
  onDelete,
  onToggleCollapse,
  onDragStart,
}) => {
  // Ensure node has a valid color (fallback to first color if missing)
  const nodeColor = node.color || NODE_COLORS[0];
  
  // For editing, we'll use the node values directly when not editing
  // and local state only when actively editing
  const [editTitle, setEditTitle] = useState(node.title || 'Untitled');
  const [editDescription, setEditDescription] = useState(node.description || '');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const titleInputRef = useRef(null);
  const nodeRef = useRef(null);

  // Handle starting edit mode
  const handleStartEdit = useCallback(() => {
    setEditTitle(node.title);
    setEditDescription(node.description);
    onStartEdit(node.id);
    // Focus after state update
    setTimeout(() => {
      if (titleInputRef.current) {
        titleInputRef.current.focus();
        titleInputRef.current.select();
      }
    }, 10);
  }, [node.id, node.title, node.description, onStartEdit]);

  const handleSave = () => {
    onUpdate(node.id, { title: editTitle, description: editDescription });
    onEndEdit();
  };

  const handleCancel = () => {
    setEditTitle(node.title);
    setEditDescription(node.description);
    onEndEdit();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      handleSave();
      onAddChild(node.id);
    }
  };

  const handleColorChange = (color) => {
    onUpdate(node.id, { color });
    setShowColorPicker(false);
  };

  const handleMouseDown = (e) => {
    if (e.button === 0 && !isEditing) {
      setIsDragging(true);
      setIsHovered(false); // Collapse card when starting drag
      onDragStart(node.id, e);
      
      // Listen for mouseup to end dragging
      const handleMouseUp = () => {
        // Small delay to prevent immediate hover trigger after drag
        setTimeout(() => {
          setIsDragging(false);
        }, 100);
        document.removeEventListener('mouseup', handleMouseUp);
      };
      document.addEventListener('mouseup', handleMouseUp);
    }
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    handleStartEdit();
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (!isEditing) {
      onSelect(node.id);
    }
  };

  // Show controls when hovered, selected, or editing
  const showControls = isHovered || isSelected || isEditing;

  return (
    <g className="mind-map-node">
      <foreignObject
        x={node.x}
        y={node.y}
        width="200"
        height="300"
        style={{ overflow: 'visible' }}
      >
        <div
          ref={nodeRef}
          className={`
            relative rounded-lg cursor-grab active:cursor-grabbing
            transition-all duration-200 ease-out
            ${isSelected ? 'ring-2 ring-purple-500 ring-offset-2 ring-offset-[#0a0a0a]' : ''}
            ${isConnectorSource ? 'ring-2 ring-green-500 ring-offset-2 ring-offset-[#0a0a0a] animate-pulse' : ''}
          `}
          style={{
            backgroundColor: nodeColor.bg,
            border: `1px solid ${isConnectorSource ? '#22c55e' : nodeColor.border}`,
            boxShadow: isSelected || isConnectorSource
              ? `0 0 20px ${isConnectorSource ? '#22c55e40' : nodeColor.border + '40'}, 0 8px 32px rgba(0,0,0,0.4)`
              : '0 4px 20px rgba(0,0,0,0.3)',
          }}
          onMouseDown={handleMouseDown}
          onMouseEnter={() => {
            // Only expand on hover if not dragging
            if (!isDragging) {
              setIsHovered(true);
            }
          }}
          onMouseLeave={() => setIsHovered(false)}
          onDoubleClick={handleDoubleClick}
          onClick={handleClick}
        >
          {/* Header with collapse/expand */}
          <div className="flex items-center gap-2 px-3 py-2">
            {hasChildren && (
              <button
                className="p-0.5 rounded hover:bg-white/10 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleCollapse(node.id);
                }}
              >
                {node.collapsed ? (
                  <ChevronRight size={12} style={{ color: nodeColor.text }} />
                ) : (
                  <ChevronDown size={12} style={{ color: nodeColor.text }} />
                )}
              </button>
            )}
            
            {isEditing ? (
              <input
                ref={titleInputRef}
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 bg-transparent border-none outline-none text-sm font-semibold"
                style={{ color: nodeColor.text }}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span
                className="flex-1 text-sm font-semibold truncate"
                style={{ color: nodeColor.text }}
              >
                {node.title}
              </span>
            )}
          </div>

          {/* Description - animated show/hide */}
          <div 
            className="overflow-hidden transition-all duration-150 ease-out"
            style={{ 
              maxHeight: showControls ? '200px' : '0px',
              opacity: showControls ? 1 : 0,
            }}
          >
            <div className="px-3 pb-2">
              {isEditing ? (
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="w-full bg-transparent border-none outline-none text-xs resize-none"
                  style={{ color: `${nodeColor.text}99` }}
                  rows={3}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <p
                  className="text-xs whitespace-pre-wrap break-words"
                  style={{ color: `${nodeColor.text}99` }}
                >
                  {node.description || 'No description'}
                </p>
              )}
            </div>
          </div>

          {/* Action buttons - animated show/hide */}
          <div 
            className="overflow-hidden transition-all duration-150 ease-out"
            style={{ 
              maxHeight: showControls ? '40px' : '0px',
              opacity: showControls ? 1 : 0,
            }}
          >
            <div 
              className="flex items-center justify-between px-2 py-1.5 border-t"
              style={{ borderColor: `${nodeColor.border}50` }}
            >
              {isEditing ? (
                <>
                  <button
                    className="p-1 rounded hover:bg-white/10 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancel();
                    }}
                    title="Cancel"
                  >
                    <X size={14} style={{ color: nodeColor.text }} />
                  </button>
                  <button
                    className="p-1 rounded hover:bg-white/10 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSave();
                    }}
                    title="Save"
                  >
                    <Check size={14} style={{ color: '#22c55e' }} />
                  </button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-1">
                    <button
                      className="p-1 rounded hover:bg-white/10 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddChild(node.id);
                      }}
                      title="Add child (Tab)"
                    >
                      <Plus size={14} style={{ color: nodeColor.text }} />
                    </button>
                    <button
                      className="p-1 rounded hover:bg-white/10 transition-colors relative"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowColorPicker(!showColorPicker);
                      }}
                      title="Change color"
                    >
                      <Palette size={14} style={{ color: nodeColor.text }} />
                    </button>
                  </div>
                  <button
                    className="p-1 rounded hover:bg-red-500/20 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(node.id);
                    }}
                    title="Delete"
                  >
                    <Trash2 size={14} className="text-red-400" />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Color Picker Popover */}
          {showColorPicker && (
            <div
              className="absolute left-0 top-full mt-2 p-2 rounded-lg bg-[#1a1d24] border border-white/10 shadow-xl z-50"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="grid grid-cols-4 gap-1.5">
                {NODE_COLORS.map((color) => (
                  <button
                    key={color.id}
                    className={`
                      w-6 h-6 rounded-md transition-transform hover:scale-110
                      ${nodeColor.id === color.id ? 'ring-2 ring-white ring-offset-1 ring-offset-[#1a1d24]' : ''}
                    `}
                    style={{ backgroundColor: color.bg, border: `1px solid ${color.border}` }}
                    onClick={() => handleColorChange(color)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </foreignObject>
    </g>
  );
};

export default MindMapNode;
